import { Avatar, Button, Div, Flex, Icon, P, Skeleton, Span } from '@dnb/eufemia';
import {
  chevron_down,
  chevron_up,
  close_medium,
  log_in_medium,
  log_out_medium,
  person_medium,
} from '@dnb/eufemia/icons';
import { useMedia } from '@dnb/eufemia/shared';
import classNames from 'classnames';
import React, { ComponentPropsWithoutRef, useState } from 'react';
import { Link, Link as RouterLink, useNavigate } from 'react-router-dom';

import { logout } from '@/auth/helpers';
import useEnterPress from '@/hooks/useEnterPress';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useReturnTo from '@/hooks/useReturnTo';
import { useUser } from '@/hooks/useUser';

import { openMenu } from '..';
import { LinkItem, MobileAccordionButton, NavigationButtonElement } from './NavigationButtonElements';

import style from './index.module.css';

import { StyledAccordion, StyledDropdown } from './index.styles';

interface MenuLink {
  title: string;
  to: string;
  underline?: boolean;
  subTitle?: string;
  isVisible?: boolean;
  isProfileMenu?: boolean;
}

export interface NavigationButtonProps {
  title: string;
  links?: MenuLink[];
  isProfileMenu?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: (props: any) => any;
  setWhichMenuIsOpen?: React.Dispatch<React.SetStateAction<openMenu>>;
}

export default function NavigationButton({
  title,
  links,
  isProfileMenu = false,
  icon,
  setWhichMenuIsOpen,
}: NavigationButtonProps) {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { isLarge } = useMedia();
  const { featureFlags } = useFeatureFlags();
  const [, setReturnTo] = useReturnTo();
  const { user, isLoggedIn, isLoading: userLoading } = useUser();
  const myProfileLink = `/profile/${featureFlags.ENABLE_TEAMS ? 'teams' : 'apps'}`;

  const handleTabulationNavigation = (e: KeyboardEvent) => {
    const target = e.target as HTMLDivElement;
    const accordionOrDropdown =
      target?.id?.startsWith('option-menu') ||
      target?.id?.startsWith('linkItem') ||
      target?.id?.startsWith('option-profile');

    if (accordionOrDropdown) {
      if (e.key === 'myProfile' || target?.textContent === 'My profile') {
        navigate(myProfileLink);
        setDropdownOpen(false);
      }
      if (e.key === 'logout' || target.textContent === 'Log out') {
        logout();
        setDropdownOpen(false);
      }

      const linkNavigate = links?.find((link) => target.textContent?.includes(link.title));

      if (linkNavigate?.to) {
        navigate(linkNavigate.to);
        setDropdownOpen(false);
      }
    }
  };

  const linkRef = useEnterPress(handleTabulationNavigation);

  if (userLoading && isProfileMenu) return <Skeleton show />;

  return (
    <Div>
      {links?.length === 1 ? (
        <NavigationButtonElement
          icon={icon}
          linkTo={links[0]!.to}
          onEnterPress={handleTabulationNavigation}
          props={{ key: links[0]!.to }}
          title={title}
        />
      ) : isProfileMenu ? (
        <div>
          {isLoggedIn && isLarge ? (
            <StyledDropdown
              action_menu
              align_dropdown="right"
              className={style['profileAvatarWrapper']}
              data={() => [
                <Link
                  className={style['profileMenuButtons']}
                  key="myProfile"
                  ref={linkRef}
                  tabIndex={0}
                  to={myProfileLink}
                >
                  <Flex.Horizontal align="center" className={style['dropdownLinkProfile']}>
                    <Flex.Item grow>
                      <P modifier="basis">My profile</P>
                    </Flex.Item>
                    <Icon icon={person_medium} />
                  </Flex.Horizontal>
                </Link>,
                <Div
                  className={isLarge ? '' : style['buttonContainer']}
                  id="logout"
                  key="logout"
                  onClick={logout}
                  ref={linkRef}
                  tabIndex={0}
                >
                  <Flex.Horizontal align="center" className={style['dropdownLinkProfile']}>
                    <Flex.Item grow>
                      <P modifier="basis">Log out</P>
                    </Flex.Item>
                    <Icon icon={log_out_medium} size={24} />
                  </Flex.Horizontal>
                </Div>,
              ]}
              icon_position="right"
              id="profile"
              on_select={handleTabulationNavigation}
              skip_portal
              triangle_position="right"
              trigger_element={(props: ComponentPropsWithoutRef<'button'>) => (
                <button {...props} className={style['Avatar']} role="button" tabIndex={0}>
                  <Avatar size="medium" variant="secondary">
                    {user?.firstName}
                  </Avatar>
                  <Icon className={style['avatarChevron']} icon={chevron_down} />
                </button>
              )}
              variant="tertiary"
            />
          ) : isLoggedIn && !isLarge ? (
            <Div className={style['profileMenuWrapper']}>
              <Div className={style['avatarWrapper']}>
                {dropdownOpen ? (
                  <Button
                    icon={close_medium}
                    icon_position="top"
                    left
                    on_click={() => {
                      setDropdownOpen(false);
                      setWhichMenuIsOpen?.(openMenu.none);
                    }}
                    size="large"
                    tabIndex={0}
                    text="Close"
                    variant="tertiary"
                  />
                ) : (
                  <button
                    className={style['Avatar']}
                    onClick={() => {
                      setDropdownOpen(!dropdownOpen);
                      setWhichMenuIsOpen?.(openMenu.ProfileDropDown);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <Avatar size="medium" variant="secondary">
                      {user?.firstName}
                    </Avatar>
                    <Icon className={style['avatarChevron']} icon={chevron_down} />
                  </button>
                )}
              </Div>
              <div className={dropdownOpen ? style['profileDropdownVisible'] : style['profileDropdownHidden']}>
                <div className={style['profileDropdown']}>
                  <div className="dnb-unstyled-list" id="profileDropdownList">
                    <Link className={style['navigationButton']} ref={linkRef} tabIndex={0} to={myProfileLink}>
                      <Flex.Horizontal align="center" className={style['dropdownLinkProfile']}>
                        <Flex.Item grow>
                          <P className={style['navigationButtonText']} modifier="basis">
                            My profile
                          </P>
                        </Flex.Item>
                        <Icon icon={person_medium} />
                      </Flex.Horizontal>
                    </Link>
                    <div className={style['navigationButton']} onClick={logout}>
                      <Flex.Horizontal align="center" className={style['dropdownLinkProfile']}>
                        <Flex.Item grow>
                          <Span className={style['navigationButtonText']}>Log out</Span>
                        </Flex.Item>
                        <Icon icon={log_out_medium} size={24} />
                      </Flex.Horizontal>
                    </div>
                  </div>
                </div>
              </div>
            </Div>
          ) : (
            <Button
              element={RouterLink}
              icon={log_in_medium}
              icon_position="top"
              on_click={() => {
                setReturnTo(location.pathname);
              }}
              size="large"
              to="/login"
              variant="tertiary"
            >
              Log in
            </Button>
          )}
        </div>
      ) : isLarge ? (
        <StyledDropdown
          action_menu
          data={
            links?.map((link) => <LinkItem key={link.to} {...link} onEnterPress={handleTabulationNavigation} />) ?? []
          }
          icon_position="right"
          id="menu"
          on_select={handleTabulationNavigation}
          skip_portal
          triangle_position="left"
          trigger_element={(props: ComponentPropsWithoutRef<'button'>) => (
            <button
              type="button"
              {...props}
              className={
                dropdownOpen
                  ? classNames(style['navigationButton'], style['navigationButtonActive'])
                  : style['navigationButton']
              }
            >
              <Div className={style['navigationIcon']}>
                <Icon icon={icon} size={24} />
              </Div>
              <Span className={style['navigationButtonText']}>{title}</Span>
              <Icon
                className={dropdownOpen ? style['chevron'] : classNames(style['chevron'], style['chevronRotate'])}
                icon={chevron_up}
              />
            </button>
          )}
          variant="tertiary"
        />
      ) : (
        <StyledAccordion
          element={(props: ComponentPropsWithoutRef<'button'>) => (
            <MobileAccordionButton
              icon={icon}
              onEnterPress={handleTabulationNavigation}
              props={props}
              showChevron
              title={title}
            />
          )}
          id="burgerMenuAccordion"
          left_component={<Icon icon={icon} size={24} />}
        >
          {links?.map((link) => <LinkItem key={link.to} {...link} onEnterPress={handleTabulationNavigation} />)}
        </StyledAccordion>
      )}
    </Div>
  );
}
